/**
 * Re-export of backend error codes for frontend consumption.
 * Keep in sync with apps/backend/src/shared/error-codes.ts.
 *
 * In practice this file is a copy of the backend's source-of-truth. The
 * frontend's `errorMessages: Record<ErrorCode, TranslationKey>` map is
 * exhaustively type-checked against this union, so any drift is caught
 * at TypeScript compile time.
 */
export const ERROR_CODES = {
  // Validation & Request
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_BODY: 'INVALID_BODY',
  INVALID_JSON: 'INVALID_JSON',
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_PATH: 'INVALID_PATH',
  INVALID_RANGE: 'INVALID_RANGE',
  INVALID_PAGINATION_TOKEN: 'INVALID_PAGINATION_TOKEN',
  INVALID_LIMIT: 'INVALID_LIMIT',
  INVALID_LINK_TYPE: 'INVALID_LINK_TYPE',
  INVALID_DATE_FORMAT: 'INVALID_DATE_FORMAT',
  INVALID_DIMENSION: 'INVALID_DIMENSION',
  INVALID_PLAN: 'INVALID_PLAN',
  INVALID_STATUS: 'INVALID_STATUS',
  DISALLOWED_FIELDS: 'DISALLOWED_FIELDS',
  MISSING_BODY: 'MISSING_BODY',
  MISSING_SHORT_ID: 'MISSING_SHORT_ID',
  MISSING_API_KEY: 'MISSING_API_KEY',
  MISSING_TOKEN: 'MISSING_TOKEN',
  MISSING_USER_ID: 'MISSING_USER_ID',
  MISSING_ADMIN_ID: 'MISSING_ADMIN_ID',
  MISSING_FEEDBACK_ID: 'MISSING_FEEDBACK_ID',
  MISSING_CAMPAIGN_ID: 'MISSING_CAMPAIGN_ID',
  NO_UPDATES: 'NO_UPDATES',
  NO_FIELDS_TO_UPDATE: 'NO_FIELDS_TO_UPDATE',
  INVALID_IS_ACTIVE: 'INVALID_IS_ACTIVE',
  INVALID_IS_DELETED: 'INVALID_IS_DELETED',

  // Authentication & Authorization
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_ALREADY_USED: 'TOKEN_ALREADY_USED',
  TOKEN_VERIFICATION_FAILED: 'TOKEN_VERIFICATION_FAILED',
  TOKEN_MISMATCH: 'TOKEN_MISMATCH',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  INVALID_CURRENT_PASSWORD: 'INVALID_CURRENT_PASSWORD',
  SAME_PASSWORD: 'SAME_PASSWORD',
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_SCOPE: 'INSUFFICIENT_SCOPE',
  API_KEY_EXPIRED: 'API_KEY_EXPIRED',
  API_KEY_REVOKED: 'API_KEY_REVOKED',
  INVALID_API_KEY: 'INVALID_API_KEY',
  INVALID_SDK_KEY: 'INVALID_SDK_KEY',
  API_ACCESS_NOT_ALLOWED: 'API_ACCESS_NOT_ALLOWED',

  // Resource Not Found
  NOT_FOUND: 'NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  APP_NOT_FOUND: 'APP_NOT_FOUND',
  LINK_NOT_FOUND: 'LINK_NOT_FOUND',
  FEEDBACK_NOT_FOUND: 'FEEDBACK_NOT_FOUND',
  PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
  SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
  EXPORT_JOB_NOT_FOUND: 'EXPORT_JOB_NOT_FOUND',
  EXPORT_LINK_NOT_FOUND: 'EXPORT_LINK_NOT_FOUND',
  CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',

  // Link Management
  LINK_DELETED: 'LINK_DELETED',
  LINK_ALREADY_DELETED: 'LINK_ALREADY_DELETED',
  LINK_ALREADY_DISABLED: 'LINK_ALREADY_DISABLED',
  LINK_ALREADY_ENABLED: 'LINK_ALREADY_ENABLED',
  LINK_LIMIT_REACHED: 'LINK_LIMIT_REACHED',
  INVALID_SHORT_ID: 'INVALID_SHORT_ID',
  SHORT_ID_ALREADY_EXISTS: 'SHORT_ID_ALREADY_EXISTS',
  CUSTOM_SHORT_ID_NOT_ALLOWED: 'CUSTOM_SHORT_ID_NOT_ALLOWED',
  FAILED_TO_GENERATE_SHORT_ID: 'FAILED_TO_GENERATE_SHORT_ID',

  // QR Code
  INVALID_FORMAT: 'INVALID_FORMAT',
  INVALID_SIZE: 'INVALID_SIZE',
  INVALID_FG_COLOR: 'INVALID_FG_COLOR',
  INVALID_BG_COLOR: 'INVALID_BG_COLOR',

  // Features & Plans
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
  FEATURE_NOT_ALLOWED: 'FEATURE_NOT_ALLOWED',
  ADVANCED_DASHBOARD_NOT_ALLOWED: 'ADVANCED_DASHBOARD_NOT_ALLOWED',
  INSTALL_TRACKING_NOT_ALLOWED: 'INSTALL_TRACKING_NOT_ALLOWED',
  CHANNEL_NOT_ALLOWED: 'CHANNEL_NOT_ALLOWED',
  MULTI_CHANNEL_NOT_ALLOWED: 'MULTI_CHANNEL_NOT_ALLOWED',
  SUBSCRIPTION_NOT_ACTIVE: 'SUBSCRIPTION_NOT_ACTIVE',
  NO_ACTIVE_SUBSCRIPTION: 'NO_ACTIVE_SUBSCRIPTION',
  CANNOT_CANCEL_FREE: 'CANNOT_CANCEL_FREE',
  CANNOT_QUEUE_ON_FREE: 'CANNOT_QUEUE_ON_FREE',
  ALREADY_ON_FREE: 'ALREADY_ON_FREE',

  // User & Admin
  USER_DELETED: 'USER_DELETED',
  USER_DISABLED: 'USER_DISABLED',
  USER_ALREADY_DELETED: 'USER_ALREADY_DELETED',
  ALREADY_VERIFIED: 'ALREADY_VERIFIED',
  EMAIL_ALREADY_VERIFIED: 'EMAIL_ALREADY_VERIFIED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ALREADY_DISABLED: 'ALREADY_DISABLED',
  NOT_DISABLED: 'NOT_DISABLED',
  SELF_DELETE_FORBIDDEN: 'SELF_DELETE_FORBIDDEN',
  SELF_DISABLE_FORBIDDEN: 'SELF_DISABLE_FORBIDDEN',
  SELF_ENABLE_FORBIDDEN: 'SELF_ENABLE_FORBIDDEN',

  // Email
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',

  // Campaign
  CAMPAIGN_ARCHIVED: 'CAMPAIGN_ARCHIVED',

  // Feedback
  FEEDBACK_LOCKED: 'FEEDBACK_LOCKED',

  // Exports
  EXPORT_DATASET_UNSUPPORTED: 'EXPORT_DATASET_UNSUPPORTED',
  DATA_EXPORT_NOT_ALLOWED: 'DATA_EXPORT_NOT_ALLOWED',
  EXPORT_DATE_RANGE_TOO_LARGE: 'EXPORT_DATE_RANGE_TOO_LARGE',
  EXPORT_DATE_RANGE_INVALID: 'EXPORT_DATE_RANGE_INVALID',
  EXPORT_QUOTA_EXCEEDED: 'EXPORT_QUOTA_EXCEEDED',
  EXPORT_CONCURRENCY_EXCEEDED: 'EXPORT_CONCURRENCY_EXCEEDED',
  EXPORT_ENQUEUE_FAILED: 'EXPORT_ENQUEUE_FAILED',

  // SDK/App
  PLATFORM_MISMATCH: 'PLATFORM_MISMATCH',
  PLATFORM_NOT_DECLARED: 'PLATFORM_NOT_DECLARED',
  SDK_KEY_LIMIT_REACHED: 'SDK_KEY_LIMIT_REACHED',

  // Subscription
  SUBSCRIPTION_DATE_OVERLAP: 'SUBSCRIPTION_DATE_OVERLAP',
  NOT_QUEUED: 'NOT_QUEUED',

  // Errors
  BAD_REQUEST: 'BAD_REQUEST',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  // Rate Limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Beta
  BETA_FEATURE_DISABLED: 'BETA_FEATURE_DISABLED',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
